<template>
  <div class="expandable-item">
    <div class="expand-item">
      <ExpandButton :expanded="expandedRef" @update:expanded="toggleExpand"/>
      <input type="checkbox" v-if="props.checkbox"
        :checked="props.checked"
        :indeterminate="props.indeterminate"
        :disabled="props.checkDisabled"
        @input="e => emit('check')"
        class="form-check-input"/>
      <button type="button" class="btn btn-inline btn-link link-dark"
        aria-expanded="false"
        :aria-controls="contentId"
        @click="toggleExpand">
        <slot name="item"></slot>
      </button>
    </div>
    <div :id="contentId" ref="expandContentRef" class="expand-content collapse" :class="{ show: props.expanded }">
      <slot name="content"></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.expandable-item {
  --expand-button-size: 16px;
  --expand-button-margin: 4px;

  .expand-item {
    display: flex;
    align-items: center;

    .expand-button {
      margin-right: var(--expand-button-margin);
    }
  }

  input[type="checkbox"] {
    margin: 0;
    margin-top: -2px;
    margin-right: 0.5rem;
    font-size: 1rem;
  }

  .expand-content {
    margin-left: calc(var(--expand-button-size) + var(--expand-button-margin));
  }
}
</style>

<script lang="ts" setup>
import { onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { Collapse } from 'bootstrap'
import { DOMUtil } from './DOMUtil';
import ExpandButton from './ExpandButton.vue'

const props = defineProps({
  checkbox: Boolean,
  checked: Boolean,
  indeterminate: Boolean,
  checkDisabled: Boolean,
  expanded: Boolean,
})

const emit = defineEmits<{
  (e: 'check'): void,
  (e: 'update:expanded', value: boolean): void,
}>()

const expandContentRef = ref<HTMLElement>()
const expandedRef = ref(props.expanded)

const contentId = DOMUtil.randomElementId("content")

watch(() => props.expanded, value => {
  if (value != expandedRef.value) {
    toggleExpand()
  }
})

onMounted(() => {
  expandContentRef.value!.addEventListener('show.bs.collapse', onExpand)
  expandContentRef.value!.addEventListener('hide.bs.collapse', onCollapse)
})

onBeforeUnmount(() => {
  expandContentRef.value!.removeEventListener('show.bs.collapse', onExpand)
  expandContentRef.value!.removeEventListener('hide.bs.collapse', onCollapse)
})

function toggleExpand() {
  new Collapse(expandContentRef.value!) // toggles on init
}

function onExpand(e: Event) {
  if (e.target == e.currentTarget) {
    expandedRef.value = true
    emit('update:expanded', true)
  }
}

function onCollapse(e: Event) {
  if (e.target == e.currentTarget) {
    expandedRef.value = false
    emit('update:expanded', false)
  }
}

</script>
